import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchCurrentBusinessCycle as fetchCurrentBusinessCycleApi } from "../../services/businessCycleApi";

// Market-wide business cycle scenario detected by AutoRyse (news + multi-agent
// consensus). Independent of portfolio selection — fetched once per app session.
export const fetchCurrentBusinessCycle = createAsyncThunk(
  "businessCycle/fetchCurrent",
  async (_, { rejectWithValue, signal }) => {
    try {
      const data = await fetchCurrentBusinessCycleApi(signal);
      return data;
    } catch (err) {
      if (err?.name === "AbortError") {
        throw err;
      }
      return rejectWithValue(err?.message || "fetch_failed");
    }
  }
);

const initialState = {
  finalScenario: null,
  confidence: null,
  consensusSummary: null,
  secondaryScenario: null,
  secondaryScenarioConditions: null,
  agentsConsidered: null,
  timestampUtc: null,
  curatedNews: [],
  status: "idle",
  error: null,
};

const businessCycleSlice = createSlice({
  name: "businessCycle",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentBusinessCycle.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchCurrentBusinessCycle.fulfilled, (state, action) => {
        if (!action.payload) {
          // AutoRyse has not produced a result yet (404) — not an error, just unavailable.
          state.status = "unavailable";
          return;
        }
        state.status = "succeeded";
        state.finalScenario = action.payload.finalScenario;
        state.confidence = action.payload.confidence;
        state.consensusSummary = action.payload.consensusSummary;
        state.secondaryScenario = action.payload.secondaryScenario;
        state.secondaryScenarioConditions = action.payload.secondaryScenarioConditions;
        state.agentsConsidered = action.payload.agentsConsidered;
        state.timestampUtc = action.payload.timestampUtc;
        state.curatedNews = action.payload.curatedNews || [];
      })
      .addCase(fetchCurrentBusinessCycle.rejected, (state, action) => {
        if (action.meta.aborted) {
          return;
        }
        state.status = "failed";
        state.error = action.payload || "unknown";
      });
  },
});

export default businessCycleSlice.reducer;

export function selectCurrentBusinessCycle(state) {
  return state?.businessCycle ?? null;
}

/** True only when a real detected scenario is available to use as a default. */
export function selectHasCurrentBusinessCycle(state) {
  const bc = state?.businessCycle;
  return Boolean(bc && bc.status === "succeeded" && bc.finalScenario);
}
